import type { Environment, PlanktonState } from './types';
import { dayMeanParW } from './plankton';
import { lightShare } from './light';

/**
 * The plankton's chlorophyll (M64), mg m⁻³: what the phytoplankton and the
 * nitrogen fixers in the mixed layer hold of it, the figure a satellite
 * sees as the sea's green. The model carries its plankton in nitrogen
 * (M52), and UVic carries no chlorophyll; a fixed ratio will not do, since
 * a phytoplankton's chlorophyll to carbon follows its light: at Station
 * ALOHA HOT's chlorophyll per particulate nitrogen nearly doubles from
 * September to December (MEASURED, M55: 0.219 to 0.405 mg per mmol).
 *
 * **The ratio** is BLING's (Galbraith, E. D., Gnanadesikan, A., Dunne, J. P.
 * and Hiscock, M. R. 2010, Biogeosciences 7: 1043, CC BY 3.0), Geider,
 * MacIntyre and Kana 1997's balanced growth as BLING diagnoses it (its Eq.
 * 8, p. 1047, iron replete):
 *
 *   θ = θmax / (1 + α θmax I / 2Pm),
 *
 * θ the chlorophyll to carbon, g g⁻¹; I the light the cells are used to,
 * W m⁻²; and Pm their light-saturated growth (BLING's Eq. 3, p. 1046), set
 * by the water's warmth and phosphate alone, iron replete as the game's
 * plankton is (M52):
 *
 *   Pm = P0 exp(κ T) PO4 / (K + PO4).
 *
 * Darker water, more chlorophyll to each gram of carbon; a faster growth
 * capacity, more. Geider et al. 1997 is unread here (its publisher's host
 * refused, M64); BLING's restatement is the source, and MIT Darwin's
 * (Dutkiewicz et al. 2015, Biogeosciences 12: 4447, Eq. A24, p. 4469) prints
 * the same form.
 *
 * **The light** is the day's sunlight averaged through the mixed layer, as
 * BLING takes it ("within the mixed layer where the irradiance is
 * vertically averaged", "smoothed over one day", p. 1046). Its code
 * (GFDL's `generic_BLING.F90`, read at raw.githubusercontent.com/NOAA-GFDL/
 * ocean_BGC, SHA-256 79049051…) takes the sunlight below 710 nm, in watts
 * (2.77e18 quanta a second to the watt, "the average energy spectrum for
 * underwater PAR": 4.600 µmol J⁻¹, the game's `MICROMOL_PER_J` 4.598, within
 * 0.05%), and moves its memory toward
 * the mixed layer's light at min(1, γ dt), γ a day⁻¹: on the game's step of
 * a day that is the day's own light. Here: the day's PAR as a mean over its
 * 24 hours (`dayMeanParW`), through the mixed layer in the game's light with
 * depth (`lightShare`: VIIRS's clarity, Argo's fading, M33), the share
 * averaged by Simpson's rule at a meter's step or less. MEASURED, M64,
 * against the exact mean (Simpson's at 200 steps a meter): within 0.05% on
 * every day of every place's mean year (layers 13.4 to 202 m, clarity 0.064
 * to 0.304 m⁻¹), and within 0.09% for clarity 0.025 to 0.3 m⁻¹ and layers
 * 3.3 to 300 m (the worst 0.084%, 0.3 m⁻¹ over 4 m), where the trapezoid
 * at a meter missed by up to 1.3% (the light's red end dies in the first
 * meters). The plankton grows in UVic's own light (M52); the
 * chlorophyll is the sea's light's.
 *
 * **The values** are BLING's as GFDL's code runs them (`generic_BLING.F90`,
 * above): θmax 0.04 g g⁻¹, κ 0.063 °C⁻¹ (Eppley's) and K 0.1 µmol kg⁻¹ as the
 * paper's Table 1 (p. 1050) prints them; and since 2017 ("Increased
 * alpha_max to match TOPAZ value for small phytoplankton decrease surface
 * Chl") α `2.4e-5 *2.77e18/6.022e17`, 1.104e-4 g C (g Chl)⁻¹ (W m⁻²)⁻¹ s⁻¹
 * (2.4e-5 per µmol of photons, turned to watts), and P0 0.938e-5 s⁻¹, "0.81
 * d-1 at 0C" (Bissinger, Montagnes and Atkinson 2008, Limnology and
 * Oceanography 53: 487). The paper's own α, 73.6e-6 per watt (the code's
 * `1.6e-5` per µmol, kept there as a comment), and P0 1.0e-5 were tuned
 * with BLING's biomass, as the code's were. Neither was tuned to UVic's, so
 * the game takes the pair its own water supports, MEASURED in review M65
 * with the plankton as it is and only the chlorophyll's pair changed:
 * Station ALOHA, its growth ceiling at HOT's own phosphate (the game's there
 * is 55% of HOT's, M60), 0.87 of HOT's chlorophyll over the year for the
 * code's pair and 1.27 for the paper's; Maui Nui, its phosphate fitted to
 * Kahe Point's measurements, 1.19 times the satellite's for the code's and
 * 1.69 for the paper's; every place against the satellite, a geometric mean
 * of 0.94 against 1.21. M64 took the paper's, judged at the station's own
 * low phosphate (0.89 against 0.59): two errors offsetting.
 * **The carbon**: the phytoplankton's and the fixers' nitrogen times UVic's
 * carbon to nitrogen as the run set it (`setocn.F`'s `redctn = 7.`, which
 * `control.in` leaves; the model's own), times carbon's 12.011 mg a
 * millimole (BLING's code's `12.011e6` µg a mole). BLING has one
 * phytoplankton; the fixers are taken at the same ratio (a choice: no model
 * read here gives them their own).
 *
 * **Diagnosed, not carried**: nothing here feeds back on the plankton, as in
 * BLING's code ("it does not enter into the uptake calculation and is only
 * used for the diagnostic chlorophyll concentration"), so the world, its saves and its
 * fingerprint are as they were.
 */

export interface ChlorophyllRates {
  /** The most chlorophyll to carbon, under the dimmest light, g Chl (g C)⁻¹. */
  thetaMax: number;
  /** The chlorophyll's quantum yield under low light, g C (g Chl)⁻¹ (W m⁻²)⁻¹ s⁻¹. */
  alphaChl: number;
  /** The light-saturated growth at 0 °C, s⁻¹, and its rise with warmth, °C⁻¹ (Eppley's). */
  growthAt0CPerS: number;
  eppleyPerC: number;
  /** Phosphate's half saturation, µmol kg⁻¹. */
  phosphateHalfSat: number;
  /** Carbon to nitrogen in the plankton, mol mol⁻¹. */
  carbonPerNitrogen: number;
}

/** BLING's (Galbraith et al. 2010) as GFDL's code runs them (`thetamax_hi`, `alpha_max`, `pc_0`, `kappa_eppley`, `k_po4`; above) and UVic's carbon to nitrogen. */
export const CHLOROPHYLL_RATES: Readonly<ChlorophyllRates> = Object.freeze({
  thetaMax: 0.04,
  alphaChl: (2.4e-5 * 2.77e18) / 6.022e17,
  growthAt0CPerS: 0.938e-5,
  eppleyPerC: 0.063,
  phosphateHalfSat: 0.1,
  carbonPerNitrogen: 7,
});

/** Carbon's milligrams to the millimole (its standard atomic weight, as BLING's code takes it). */
const MG_C_PER_MMOL = 12.011;

/** The day's PAR as a mean over its 24 hours and through the mixed layer, W m⁻² (above: Simpson's rule, an even count of steps of a meter or less). */
export function mixedLayerParW(env: Environment): number {
  const h = env.mixedLayerM;
  const steps = 2 * Math.max(1, Math.ceil(h / 2));
  let sum = 0;
  for (let i = 0; i <= steps; i++) sum += lightShare(env.kdPar, (h * i) / steps) * (i === 0 || i === steps ? 1 : i % 2 === 1 ? 4 : 2);
  return (dayMeanParW(env) * sum) / (3 * steps);
}

/**
 * The phytoplankton's chlorophyll to carbon on the day, g g⁻¹ (above): at
 * `phosphate`, mmol m⁻³, in water of `densityKgM3`, kg m⁻³, which turns
 * BLING's half saturation from per kilogram to the model's per cubic meter.
 * In the dark, the most; with no phosphate to grow on, none.
 */
export function chlorophyllPerCarbon(env: Environment, phosphate: number, densityKgM3: number, rates: ChlorophyllRates = CHLOROPHYLL_RATES): number {
  const light = mixedLayerParW(env);
  if (!(light > 0)) return rates.thetaMax;
  // µmol kg⁻¹ times kg m⁻³ is µmol m⁻³, a thousand to the model's mmol m⁻³ (a definition).
  const halfSat = (rates.phosphateHalfSat * densityKgM3) / 1000;
  const growth = rates.growthAt0CPerS * Math.exp(rates.eppleyPerC * env.seaSurfaceC) * (phosphate / (halfSat + phosphate));
  return rates.thetaMax / (1 + (rates.alphaChl * rates.thetaMax * light) / (2 * growth));
}

/** The mixed layer's chlorophyll on the day, mg m⁻³: the phytoplankton's and the fixers' carbon at the day's ratio (above). */
export function chlorophyllOf(p: PlanktonState, env: Environment, densityKgM3: number, rates: ChlorophyllRates = CHLOROPHYLL_RATES): number {
  return chlorophyllPerCarbon(env, p.phosphate, densityKgM3, rates) * (p.phyto + p.fixers) * rates.carbonPerNitrogen * MG_C_PER_MMOL;
}
